import React, { CSSProperties, FC, PropsWithChildren } from 'react';
import styled, { css } from 'styled-components';
import { fontSize, fontWeight, mediaMax, secondaryColor, statusColor, tertiaryColor } from '../../utils';

const FormFieldContainer = styled.div<{ error: boolean }>`
  &:not(:last-child) {
    padding-bottom: 15px;
  }

  > label {
    display: block;
    padding-bottom: 5px;
    font-size: ${fontSize.medium};
    font-weight: ${fontWeight.semiBold};
    color: ${tertiaryColor(900)};

    &[for] {
      cursor: pointer;
    }

    ${props =>
      props.error &&
      css`
        color: ${statusColor('error')};
      `};
  }
`;

const FormFieldError = styled.p`
  margin-top: 5px;
  font-size: ${fontSize.small};
  color: ${secondaryColor(400)};
`;

interface FormFieldProps {
  label: string;
  htmlFor?: string;
  error?: boolean | string;
  required?: boolean;
  className?: string;
  style?: CSSProperties;
  errorStyle?: CSSProperties;
}

export const FormField: FC<PropsWithChildren<FormFieldProps>> = ({
  label,
  htmlFor,
  error,
  required,
  style,
  errorStyle,
  className,
  children,
}) => (
  <FormFieldContainer error={!!error} className={className} style={style}>
    <label htmlFor={htmlFor}>
      {label} {required ? ' *' : null}
    </label>
    {children}
    {typeof error === 'string' && <FormFieldError style={errorStyle}>{error}</FormFieldError>}
  </FormFieldContainer>
);

export const DoubleFormField = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-column-gap: 16px;

  > div:not(:last-child) {
    padding-bottom: 0;
  }

  &:not(:last-child) {
    padding-bottom: 15px;
  }

  @media screen and (${mediaMax.tablet}) {
    grid-template-columns: 1fr;

    > div:not(:last-child) {
      padding-bottom: 15px;
    }
  }
`;

export const FormGroup = styled.div<{ $columns?: number; $columnGap?: number; $rowGap?: number }>`
  display: grid;
  grid-template-columns: repeat(${props => props.$columns ?? '1'}, 1fr);
  column-gap: ${props => props.$columnGap ?? '35px'};
  row-gap: ${props => props.$rowGap ?? '15px'};
  width: 100%;

  @media screen and (${mediaMax.tablet}) {
    grid-template-columns: 1fr;
    row-gap: 24px;
  }
`;
